import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, X, Download } from 'lucide-react';
import { useToast, storage, getTenantUsersKey } from '../shared.jsx';
import { AnimalAvatar, getAnimalName } from './AnimalAvatars.jsx';
import { useDialogA11y } from './ModalDialog.jsx';

// Build participant display names, handling duplicates
function getDisplayNames(conflict) {
  const mentees = conflict.mentees || [];
  const p1 = mentees[0] || {};
  const p2 = mentees[1] || {};
  
  let name1 = p1.avatar ? getAnimalName(p1.avatar) : (p1.name || 'Participant 1');
  let name2 = p2.avatar ? getAnimalName(p2.avatar) : (p2.name || 'Participant 2');
  
  if ((p1.avatar || 'bunny') === (p2.avatar || 'fox')) {
    const first1 = p1.name?.split(' ')[0] || 'A';
    const first2 = p2.name?.split(' ')[0] || 'B';
    name1 = `${name1} (${first1})`;
    name2 = `${name2} (${first2})`;
  }
  
  return {
    name1, name2,
    animal1: p1.avatar || 'bunny',
    animal2: p2.avatar || 'fox',
    id1: p1.id,
    id2: p2.id
  };
}

// Pick a random item from an array
function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Generate a story from conflict data — uses animal names for warmth
function generateStoryFromData(conflict) {
  const { name1, name2, id1, id2 } = getDisplayNames(conflict);
  const paragraphs = [];

  // Title and Setup — use animal names
  const title = conflict.title || 'a disagreement';
  const openings = [
    `${name1} and ${name2} found themselves at odds over ${title.toLowerCase()}. It was something that mattered deeply to both of them, so they decided to sit down together and work through it.`,
    `It started with ${title.toLowerCase()} \u2014 a disagreement that neither ${name1} nor ${name2} could ignore. Rather than let it fester, they chose to face it together.`,
    `When ${title.toLowerCase()} came between ${name1} and ${name2}, they made a brave choice: to talk it through honestly, step by step, instead of letting it pull them apart.`,
  ];
  paragraphs.push(pick(openings));

  // Phase 1: Identify and Define — paraphrases
  const idData = conflict.steps?.identifyDefine?.data;
  if (idData) {
    const entries = Object.entries(idData);
    if (entries.length >= 2) {
      // Match entries to participants by ID so the right name goes with the right quote
      const d1 = idData[id1] || entries[0]?.[1];
      const d2 = idData[id2] || entries[1]?.[1];

      if (d1?.paraphrase && d2?.paraphrase) {
        const idPhrases = [
          `First, they each put the problem into their own words. ${name1} understood it as: \u201C${d1.paraphrase}\u201D ${name2} saw it differently: \u201C${d2.paraphrase}\u201D Just hearing each other\u2019s perspective helped them realize this wasn\u2019t as simple as it seemed.`,
          `They started by describing the problem as they each understood it. ${name1} said: \u201C${d1.paraphrase}\u201D While ${name2} described it as: \u201C${d2.paraphrase}\u201D Already, the gap between their views was becoming clearer.`,
          `Before anything else, they each tried to name the problem. \u201C${d1.paraphrase}\u201D said ${name1}. \u201C${d2.paraphrase}\u201D said ${name2}. Two perspectives on the same situation \u2014 and neither was wrong.`,
        ];
        paragraphs.push(pick(idPhrases));
      }
    }
  }

  // Phase 2: Communicate — Because statements (use "one" and "the other")
  const cycles = conflict.steps?.communicate?.cycles || [];
  if (cycles.length > 0) {
    const statements = [];
    cycles.forEach(cycle => {
      if (cycle.statement) {
        statements.push(cycle.statement);
      }
    });

    if (statements.length >= 2) {
      const commOpenings = [
        `Then came the harder part \u2014 sharing how they really felt.`,
        `Next, ${name1} and ${name2} opened up about what was underneath the disagreement.`,
        `With the problem named, they moved on to something harder: sharing their feelings.`,
      ];
      paragraphs.push(
        `${pick(commOpenings)} ${name1} took a deep breath and said, \u201C${statements[0]}\u201D ${name2} listened carefully before responding, \u201C${statements[1]}\u201D`
      );

      if (statements.length > 2) {
        const extras = statements.slice(2).map(s => `\u201C${s}\u201D`).join(' Then: ');
        const continuations = [
          `Back and forth they went, each time understanding a little more. ${extras}`,
          `The conversation continued, layer by layer. ${extras}`,
          `With each exchange, something shifted. ${extras}`,
        ];
        paragraphs.push(pick(continuations));
      }

      const paraphrased = cycles.filter(c => c.paraphrase);
      if (paraphrased.length > 0 && paraphrased.length < cycles.length) {
        paragraphs.push('They have begun reflecting back what they heard. Some statements are still awaiting a paraphrase.');
      }
      if (paraphrased.length > 0 && paraphrased.length === cycles.length) {
        const listenPhrases = [
          `What made the difference was how they listened \u2014 not just hearing the words, but truly trying to understand. After each sharing, the other would reflect back what they heard, making sure nothing was lost.`,
          `The key wasn\u2019t just speaking \u2014 it was listening. They took turns reflecting back what the other said, proving they\u2019d really heard it.`,
          `Between each statement, something important happened: the other person paraphrased what they\u2019d heard. It was a small act, but it changed everything.`,
        ];
        paragraphs.push(pick(listenPhrases));
      }
    } else if (statements.length === 1) {
      paragraphs.push(
        `${name1} found the courage to share what was on their heart: \u201C${statements[0]}\u201D It wasn\u2019t easy, but it was a start.`
      );
    }
  }

  // Phase 3: Explore Alternatives
  const altData = conflict.steps?.exploreAlternatives?.data;
  if (altData) {
    const allAlts = [];
    Object.entries(altData).forEach(([, data]) => {
      if (data.alternatives) {
        data.alternatives.forEach(alt => {
          allAlts.push(alt.text || alt);
        });
      }
    });

    if (allAlts.length > 0) {
      const altIntros = [
        `With a better understanding of each other, they brainstormed ideas.`,
        `Now that they\u2019d truly heard each other, it was time to think about solutions.`,
        `Understanding led to creativity. Together, they began imagining ways forward.`,
      ];
      const altList = allAlts.map(a => `\u201C${a}\u201D`).join(', ');
      paragraphs.push(
        `${pick(altIntros)} The ideas that emerged included: ${altList}. Each one showed they were thinking about both sides, not just their own.`
      );
    }
  }

  // Phase 4 and 5: Resolution
  const selectedAlt = conflict.steps?.evaluateSelect?.selectedAlternative;
  if (selectedAlt) {
    const resText = selectedAlt.text || selectedAlt;
    const resPhrases = [
      `After weighing all the options, ${name1} and ${name2} agreed on a path forward: \u201C${resText}\u201D It wasn\u2019t about winning or losing \u2014 it was about finding something they could both believe in.`,
      `In the end, one solution stood out to both ${name1} and ${name2}: \u201C${resText}\u201D A resolution built not from compromise, but from genuine understanding.`,
      `Together, ${name1} and ${name2} chose their resolution: \u201C${resText}\u201D It felt right because they\u2019d both been truly heard along the way.`,
    ];
    paragraphs.push(pick(resPhrases));
  }

  // Closing — depends on phase
  const isResolved = conflict.status === 'resolved' || conflict.status === 'follow-up';
  if (isResolved) {
    const closings = [
      `The disagreement hadn\u2019t disappeared \u2014 but it had transformed into understanding. ${name1} and ${name2} walked away not just with a solution, but with a stronger bond than before.`,
      `What started as a conflict ended as a conversation. And what ${name1} and ${name2} found wasn\u2019t just a resolution \u2014 it was a deeper respect for each other.`,
      `In the end, the hardest part wasn\u2019t finding a solution. It was choosing to listen. And because ${name1} and ${name2} did, everything changed.`,
    ];
    paragraphs.push(pick(closings));
  } else {
    const phase = conflict.status;
    if (phase === 'communicate') {
      paragraphs.push(pick([
        `${name1} and ${name2}\u2019s conversation continues, and with each exchange, the path forward becomes a little clearer. The story isn\u2019t over yet \u2014 but the hardest part, choosing to listen, has already begun.`,
        `They\u2019re still talking it through. And that\u2019s the point \u2014 ${name1} and ${name2} haven\u2019t walked away. The willingness to stay is itself a kind of resolution.`,
      ]));
    } else if (phase === 'explore-alternatives' || phase === 'evaluate-select') {
      paragraphs.push(pick([
        `Together, ${name1} and ${name2} are working toward a solution \u2014 proof that even the toughest disagreements can be untangled when two hearts are willing to try.`,
        `The conversation has shifted from \u201Cwhat went wrong\u201D to \u201Cwhat could go right.\u201D A resolution is within reach for ${name1} and ${name2}.`,
      ]));
    } else if (phase === 'agree-implement') {
      paragraphs.push(`A resolution is within reach. ${name1} and ${name2} are close to turning this conflict into a story with a hopeful ending.`);
    } else {
      paragraphs.push(`And so ${name1} and ${name2}\u2019s journey toward understanding has begun. Every great resolution starts with the simple willingness to sit down and listen.`);
    }
  }

  return paragraphs.join('\n\n');
}

// === PDF DOWNLOAD ===
async function downloadStoryPDF(story, name1, name2, conflictTitle) {
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 25;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Header bar
  doc.setFillColor(251, 191, 36);
  doc.rect(0, 0, pageWidth, 35, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(120, 53, 15);
  doc.text('The Story of', margin, 18);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(146, 64, 14);
  doc.text(`${name1} & ${name2}`, margin, 28);

  y = 45;
  doc.setFontSize(10);
  doc.setTextColor(120, 113, 108);
  doc.setFont('helvetica', 'italic');
  doc.text(`From The ArguMentor \u2022 ${conflictTitle || 'A Conflict Resolution Journey'}`, margin, y);

  y += 5;
  doc.setDrawColor(251, 191, 36);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;

  // Story body
  doc.setFont('times', 'normal');
  doc.setFontSize(12);
  doc.setTextColor(68, 64, 60);
  const lineHeight = 6;

  const paragraphTexts = story.split('\n\n');
  for (let pi = 0; pi < paragraphTexts.length; pi++) {
    const paragraph = paragraphTexts[pi];
    const lines = doc.splitTextToSize(paragraph, contentWidth);

    for (let li = 0; li < lines.length; li++) {
      if (y + lineHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(lines[li], margin, y);
      y += lineHeight;
    }
    y += 4;
  }

  // Footer
  y = Math.max(y + 10, pageHeight - 30);
  if (y > pageHeight - 15) {
    doc.addPage();
    y = pageHeight - 30;
  }
  doc.setDrawColor(251, 191, 36);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  doc.setTextColor(168, 162, 158);
  doc.text('Generated by The ArguMentor \u2022 Resolve conflicts with clarity', margin, y);
  doc.text(`${new Date().toLocaleDateString()}`, pageWidth - margin, y, { align: 'right' });

  const filename = `Story-${name1}-and-${name2}.pdf`.replace(/[^a-zA-Z0-9\-_.()]/g, '_');
  doc.save(filename);
}

// === STORY MODE BUTTON ===
function StoryModeButton({ conflict, user, compact = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [story, setStory] = useState(null);

  const [profiles, setProfiles] = useState([]);
  useEffect(() => {
    let cancelled = false;
    storage.get(getTenantUsersKey(user?.tenantId || 'tenant-public'), true)
      .then(records => { if (!cancelled) setProfiles(Object.values(records || {}).filter(Boolean)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.tenantId, conflict.id]);
  const displayConflict = { ...conflict, mentees: (conflict.mentees || []).map(person => {
    const profile = person.id === user?.id ? user : profiles.find(item => item.id === person.id);
    return { ...person, avatar: profile?.avatar || person.avatar };
  }) };

  const handleOpen = () => {
    const text = generateStoryFromData(displayConflict);
    setStory(text);
    setIsOpen(true);
  };

  const { name1, name2, animal1, animal2 } = getDisplayNames(displayConflict);

  if (compact) {
    return (
      <>
        <button
          onClick={handleOpen}
          className="flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg border border-amber-200 transition-colors"
        >
          <BookOpen className="w-4 h-4" />
          View as Story
        </button>
        {isOpen && (
          <StoryModal
            story={story}
            name1={name1}
            name2={name2}
            animal1={animal1}
            animal2={animal2}
            conflictTitle={conflict.title}
            onClose={() => setIsOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex -space-x-2">
            <AnimalAvatar animal={animal1} size={32} />
            <AnimalAvatar animal={animal2} size={32} />
          </div>
          <div>
            <h4 className="font-medium text-amber-900 flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Story Mode
            </h4>
            <p className="text-xs text-amber-700">See your conflict as a story between forest friends</p>
          </div>
        </div>
        <button
          onClick={handleOpen}
          className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-lg text-sm font-medium transition-colors shadow-sm"
        >
          View as Story
        </button>
      </div>

      {isOpen && (
        <StoryModal
          story={story}
          name1={name1}
          name2={name2}
          animal1={animal1}
          animal2={animal2}
          conflictTitle={conflict.title}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

// === STORY MODAL ===
function StoryModal({ story, name1, name2, animal1, animal2, conflictTitle, onClose }) {
  const dialogRef = useRef(null);
  useDialogA11y(dialogRef, { onClose });
  const [downloading, setDownloading] = useState(false);
  const toast = useToast();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadStoryPDF(story, name1, name2, conflictTitle);
    } catch (err) {
      console.error('PDF download error:', err);
      toast('Could not download PDF. Please try again.');
    }
    setDownloading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" ref={dialogRef} role="dialog" aria-modal="true" aria-label="Conflict story" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-5 border-b border-amber-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-3">
                <AnimalAvatar animal={animal1} size={44} />
                <AnimalAvatar animal={animal2} size={44} />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900 text-lg">The Story of</h3>
                <p className="text-sm text-amber-700">{name1} & {name2}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-amber-700 hover:text-amber-800 p-1" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 200px)' }}>
          {story ? (
            <div className="text-stone-700 leading-relaxed font-serif text-base space-y-4">
              {story.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-stone-500">
              <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No story data available yet.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-stone-200 bg-stone-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {story && (
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-3 py-2 rounded-lg border border-amber-200 transition-colors disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" />
                  {downloading ? 'Saving...' : 'Download PDF'}
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-stone-900 rounded-lg text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
          <p className="text-xs text-stone-500 italic mt-2 text-center">Both participants see the same story</p>
        </div>
      </div>
    </div>
  );
}

export { StoryModeButton, StoryModal, generateStoryFromData, getDisplayNames };
export default StoryModeButton;
